/**
 * @file AmendmentStateBanner.jsx
 * @module ui/src/components/tenant-admin/AmendmentStateBanner
 * @description
 *   Renders the persistent amendment-mode context for a plan document, keeping
 *   the approved governing version visibly distinct from the proposed draft.
 *
 * @version 1.0.1
 * @since S755
 * @author data-fit LLC · DecisionFlow · S776
 *
 * @architecture
 *   Locked decisions : #1, #2, #25
 *   Temporal invariants : INV-1, INV-3
 *   Design principles : DP-1, DP-2, DP-14
 *   Cross-cutting contracts : XC-7
 *
 * @domain-rules
 *   - The approved version remains governing while an amendment is in flight, so that fact is always stated explicitly.
 *   - The amendment effective date is an economic effective date, not an approval or submission date.
 *   - Workflow status is displayed as workflow context only and never used to infer temporal applicability.
 *   - Version switching and before/after comparison are delegated to shared version components rather than reimplemented here.
 *
 * @session-6-migration-flags
 *   - plan.effective_from — pending m_tc_01
 *   - plan.effective_to — pending m_tc_01
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group E batch delivery
 *   1.0.1  S756  OI-S755-C21-REACT-IMPORT-01 — added `import React from 'react'`
 *                as defensive measure: required under classic JSX transform (React 16
 *                or explicit classic config); harmless under new JSX transform
 *                (React 17+). All other SME-built files in this batch include the
 *                React default import; this file was the sole exception.
 */

// WHY: React must be in scope when using JSX under the classic JSX transform
// (React 16 or projects with an explicit jsxRuntime: 'classic' config). Under
// the new JSX transform (React 17+) this import is harmless but not required.
// Added defensively because this was the only file in the batch missing it,
// and the project's transform version is unconfirmed at component-build time.
// (OI-S755-C21-REACT-IMPORT-01)
import React from 'react';
import PropTypes from 'prop-types';

import PlanVersionSwitcher from '../shared/PlanVersionSwitcher';
import PlanVersionCompare from '../shared/PlanVersionCompare';

/**
 * @typedef {Object} AmendmentContext
 * @property {string} amendmentId
 * @property {number} draftVersion
 * @property {string} effectiveDate
 * @property {string} reason
 * @property {'draft'|'pending_approval'} amendmentStatus
 * @property {number} approvedVersion
 * @property {string} approvedEffectiveFrom
 * @property {string|null} approvedEffectiveTo
 */

/**
 * @typedef {Object} VersionOption
 * @property {string} value
 * @property {string} label
 */

/**
 * @typedef {Object} AmendmentStateBannerProps
 * @property {AmendmentContext} amendment
 * @property {'approved'|'draft'} activeVersion
 * @property {boolean} compareActive
 * @property {boolean} isActor
 * @property {Function} onVersionSwitch
 * @property {Function} onCompareToggle
 * @property {Function} onCancelAmendment
 * @property {Function} onSubmitAmendment
 */

const styles = {
  banner: {
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    background: 'rgb(var(--color-surface))',
    borderBottom: '1px solid rgb(var(--color-border))',
    padding: '16px 20px',
    color: 'rgb(var(--color-text))',
  },
  topRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    flexWrap: 'wrap',
  },
  identity: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  title: {
    fontWeight: 600,
  },
  statusBadge: {
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '0.8125rem',
    background: 'rgb(var(--color-surface-muted))',
  },
  governing: {
    fontWeight: 600,
  },
  secondary: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.875rem',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: '14px',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    marginLeft: 'auto',
    flexWrap: 'wrap',
  },
};

/**
 * Persistent amendment-state banner.
 *
 * @param {AmendmentStateBannerProps} props
 * @returns {JSX.Element}
 */
export default function AmendmentStateBanner({
  amendment,
  activeVersion,
  compareActive,
  isActor,
  onVersionSwitch,
  onCompareToggle,
  onCancelAmendment,
  onSubmitAmendment,
}) {
  // WHY: Workflow state must be communicated in text because color alone is not an accessible or domain-safe discriminator.
  const amendmentStatusLabel =
    amendment.amendmentStatus === 'pending_approval'
      ? 'Pending Review'
      : 'Draft';

  // WHY: Version options preserve the explicit governing-versus-proposed distinction required during amendment review.
  const versions = [
    {
      value: 'approved',
      label: `Approved V${amendment.approvedVersion} · Governing`,
    },
    {
      value: 'draft',
      label: `Draft V${amendment.draftVersion} · Proposed change`,
    },
  ];

  // WHY: The shared comparison component needs explicit base and comparison identities without owning temporal inference.
  const approvedVersion = {
    value: 'approved',
    version: amendment.approvedVersion,
    label: `Approved V${amendment.approvedVersion}`,
  };

  // WHY: The draft is represented independently so it can never visually collapse into the governing version.
  const draftVersion = {
    value: 'draft',
    version: amendment.draftVersion,
    label: `Draft V${amendment.draftVersion}`,
  };

  return (
    <section
      style={styles.banner}
      aria-label="Plan amendment state"
      data-component="AmendmentStateBanner"
    >
      <div style={styles.topRow}>
        <div style={styles.identity}>
          <div style={styles.titleRow}>
            <span style={styles.title}>
              Draft amendment V{amendment.draftVersion}
            </span>

            <span style={styles.statusBadge}>
              {amendmentStatusLabel}
            </span>
          </div>

          <div>
            Economic effective date: {amendment.effectiveDate}
          </div>

          <div style={styles.governing}>
            Approved V{amendment.approvedVersion} remains governing from{' '}
            {amendment.approvedEffectiveFrom} onward
          </div>

          <div style={styles.secondary}>
            Approved version effective range:{' '}
            {amendment.approvedEffectiveFrom} →{' '}
            {amendment.approvedEffectiveTo || 'open-ended'}
          </div>

          <div style={styles.secondary}>
            Amendment reason: {amendment.reason}
          </div>
        </div>
      </div>

      <div style={styles.controls}>
        <PlanVersionSwitcher
          versions={versions}
          activeVersion={activeVersion}
          onSwitch={onVersionSwitch}
        />

        <PlanVersionCompare
          baseVersion={approvedVersion}
          compareVersion={draftVersion}
          active={compareActive}
          onToggle={onCompareToggle}
        />

        {/* WHY: Governance actions are exposed only to an authorized actor; non-actors retain full amendment context without mutation controls. */}
        {/* S818: Submit amendment hidden when pending_approval — only valid from open|rejected (spec §1.57 state machine). */}
        {isActor && (
          <div style={styles.actions}>
            <button
              type="button"
              className="text-button text-button--danger"
              onClick={onCancelAmendment}
            >
              Cancel amendment
            </button>

            {amendment.amendmentStatus !== 'pending_approval' && (
              <button
                type="button"
                className="primary-button"
                onClick={onSubmitAmendment}
              >
                Submit amendment
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

AmendmentStateBanner.propTypes = {
  amendment: PropTypes.shape({
    amendmentId: PropTypes.string.isRequired,
    draftVersion: PropTypes.number.isRequired,
    effectiveDate: PropTypes.string.isRequired,
    reason: PropTypes.string.isRequired,
    amendmentStatus: PropTypes.oneOf(['draft', 'pending_approval']).isRequired,
    approvedVersion: PropTypes.number.isRequired,
    approvedEffectiveFrom: PropTypes.string.isRequired,
    approvedEffectiveTo: PropTypes.string,
  }).isRequired,
  activeVersion: PropTypes.oneOf(['approved', 'draft']).isRequired,
  compareActive: PropTypes.bool.isRequired,
  isActor: PropTypes.bool.isRequired,
  onVersionSwitch: PropTypes.func.isRequired,
  onCompareToggle: PropTypes.func.isRequired,
  onCancelAmendment: PropTypes.func.isRequired,
  onSubmitAmendment: PropTypes.func.isRequired,
};

/**
 * @example
 * <AmendmentStateBanner
 *   amendment={{
 *     amendmentId: '1b35ea4d-53bd-43e1-9fad-c4d721f73572',
 *     draftVersion: 4,
 *     effectiveDate: '2026-07-01',
 *     reason: 'Rebalance accelerator economics for enterprise sellers',
 *     amendmentStatus: 'draft',
 *     approvedVersion: 3,
 *     approvedEffectiveFrom: '2026-01-01',
 *     approvedEffectiveTo: null,
 *   }}
 *   activeVersion="draft"
 *   compareActive={false}
 *   isActor
 *   onVersionSwitch={setActiveVersion}
 *   onCompareToggle={setCompareActive}
 *   onCancelAmendment={() => setCancelOpen(true)}
 *   onSubmitAmendment={() => setReadinessOpen(true)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - DP-2: Approved governing version is explicitly stated whenever this banner renders.
 * - INV-1: Amendment effectiveDate is labeled "Economic effective date".
 * - INV-3: amendmentStatus is presented only as workflow context.
 * - DP-14/XC-7: Workflow state appears as readable text, not color alone.
 * - C-60/C-61: Version switching and comparison are delegated to shared components.
 * - OI-S755-C21-REACT-IMPORT-01: React default import added for classic JSX transform
 *   compatibility; harmless under new JSX transform (React 17+).
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - plan.effective_from — pending m_tc_01.
 * - plan.effective_to — pending m_tc_01.
 */

/**
 * ACCESSIBILITY NOTES
 * - Banner is exposed as a labeled section.
 * - Amendment workflow status is named in text.
 * - Governing/draft version distinctions are explicit textual labels.
 * - Action controls use native button semantics and established button classes.
 */